import axios from "axios";
import { verifiedHexTrustEndpoint } from "../../constants";
import { ethers } from "ethers";
import { chainDetails } from "../../config";

export const hexTrustVaults = async (
  chainId,
  accountNamePrefix = "",
  accountNameSuffix = "",
  limit = "",
  offset = "",
  sort = ""
) => {
  return await axios({
    method: "GET",
    url: `${verifiedHexTrustEndpoint}/vaults?chainId=${chainId}&accountNamePrefix=${accountNamePrefix}&accountNameSuffix=${accountNameSuffix}&limit=${limit}&offset=${offset}&sort=${sort}`,
  })
    .then((res) => {
      if (res?.data?.body?.vaults) {
        return res?.data?.body?.vaults;
      } else {
        return [];
      }
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error(
          "fetch hextrust vaults failed with error: ",
          err?.message
        );
      }

      return [];
    });
};

export const hexTrustVaultAddress = async (vaultId, chainId) => {
  return await axios({
    method: "GET",
    url: `${verifiedHexTrustEndpoint}/vault-address?vaultId=${vaultId}&chainId=${chainId}`,
  })
    .then((res) => {
      if (res?.data?.body?.addresses?.addressList) {
        return res?.data?.body?.addresses?.addressList;
      } else {
        return [];
      }
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error(
          "fetch hextrust vault addresses failed with error: ",
          err?.message
        );
      }

      return [];
    });
};

export const hexTrustGasConfig = async (
  chainId,
  from,
  to,
  assetKey,
  nativeTokenAmount
) => {
  return await axios({
    method: "GET",
    url: `${verifiedHexTrustEndpoint}/gas?chainId=${chainId}&from=${from}&to=${to}&assetKey=${assetKey}&valueDecimal=${nativeTokenAmount}`,
  })
    .then((res) => {
      if (res?.data?.body?.data) {
        return res?.data?.body?.data;
      } else {
        return null;
      }
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error(
          "fetch hextrust gas configuration failed with error: ",
          err?.message
        );
      }

      return null;
    });
};

export const hexTrustTransactionReceipt = async (chainId, traceId) => {
  return await axios({
    method: "GET",
    url: `${verifiedHexTrustEndpoint}/transactions?chainId=${chainId}&traceId=${traceId}`,
  })
    .then((res) => {
      if (res?.data?.body?.tx) {
        return res?.data?.body?.tx;
      } else {
        return null;
      }
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error(
          "fetch hextrust gas configuration failed with error: ",
          err?.message
        );
      }

      return null;
    });
};

export const hexTrustSendTransaction = async (
  userAddres,
  provider,
  txNonce,
  contractAddress,
  chainId,
  vaultId,
  nativeTokenAmountInEther,
  txData,
  txNote
) => {
  if (userAddres && contractAddress && chainId && vaultId) {
    const assetKey = chainDetails[chainId]?.hexTrustNativeTokenId
      ? chainDetails[chainId]?.hexTrustNativeTokenId
      : `${chainId}_ETH`;
    const transactionGasRes = await hexTrustGasConfig(
      chainId,
      userAddres,
      contractAddress,
      assetKey,
      nativeTokenAmountInEther
    );
    const userBalance = await provider.getBalance(userAddres);
    let balanceStatus;

    if (
      Number(userBalance) >
      Number(
        Number(transactionGasRes?.fast?.gasLimit) *
          Number(transactionGasRes?.fast?.maxFeePerGas)
      )
    ) {
      balanceStatus = "average";
    } else if (
      Number(userBalance) >
      Number(
        Number(transactionGasRes?.average?.gasLimit) *
          Number(transactionGasRes?.average?.maxFeePerGas)
      )
    ) {
      balanceStatus = "average";
    } else if (
      Number(userBalance) >
      Number(
        Number(transactionGasRes?.slow?.gasLimit) *
          Number(transactionGasRes?.slow?.maxFeePerGas)
      )
    ) {
      balanceStatus = "slow";
    } else {
      balanceStatus = null;
    }

    const transactionGas = balanceStatus
      ? transactionGasRes[balanceStatus]
      : null;
    if (transactionGas) {
      const nativeTokenAmountInWei = ethers.utils
        .parseUnits(nativeTokenAmountInEther.toString(), 18)
        ?.toString();
      return await axios({
        method: "POST",
        url: `${verifiedHexTrustEndpoint}/transactions`,
        data: {
          txBody: {
            action: "contract-interaction",
            chainId: String(chainId),
            chainSpecific: txData
              ? {
                  data: txData,
                  type: "2",
                  value:
                    Number(nativeTokenAmountInEther) <= 0
                      ? "0x0"
                      : `0x${nativeTokenAmountInWei?.toString(16)}`,

                  nonce: txNonce,
                  to: contractAddress,

                  chainId: chainId,
                  ...transactionGas,
                }
              : {
                  type: "2",
                  value:
                    Number(nativeTokenAmountInEther) <= 0
                      ? "0x0"
                      : `0x${nativeTokenAmountInWei?.toString(16)}`,

                  nonce: txNonce,
                  to: contractAddress,

                  chainId: chainId,
                  ...transactionGas,
                },
            enterpriseId: process.env.REACT_APP_HEX_TRUST_ENTERPRISE_ID,
            from: userAddres,
            note: txNote,
            originUrl: window.location.href,
            to: contractAddress,
            vaultId: vaultId,
            amount: {
              assetKey: chainDetails[chainId]?.hexTrustNativeTokenId
                ? chainDetails[chainId]?.hexTrustNativeTokenId
                : `${chainId}_ETH`,
              valueDecimal:
                Number(nativeTokenAmountInEther) > 0
                  ? String(nativeTokenAmountInEther)
                  : "0",
            },
          },
          chainId: chainId,
        },
      })
        .then(async (res) => {
          if (res?.data?.body?.data?.traceId) {
            const traceId = res?.data?.body?.data?.traceId;
            const maxRound = process.env
              .HEX_TRUST_API_KEY_DEV_TRANSACTION_MAX_ROUND
              ? Number(process.env.HEX_TRUST_API_KEY_DEV_TRANSACTION_MAX_ROUND)
              : 60; //60 rounds(30 mins) minute default???

            const errorStatuses = [
              "denied",
              "error",
              "unknown",
              "Superseded",
              "onchainfailed",
              "failed",
              "orphaned",
              "cancelled",
              "offchaincancelled",
            ];
            for (let i = 0; i < maxRound; i++) {
              await new Promise((resolve) => setTimeout(resolve, 30000)); // 30 second delay

              console.log(
                "Timeout exceeded, fetching transaction receipt for round:",
                i + 1,
                "out of",
                Number(maxRound)
              );

              const transaction = await hexTrustTransactionReceipt(
                chainId,
                traceId
              );

              if (
                transaction?.secondaryTransactionStatus?.toLowerCase() ===
                "completed"
              ) {
                // successToast("Transaction Successful", `${txNote} successful`);
                return "completed";
              } else if (
                errorStatuses.includes(
                  transaction?.secondaryTransactionStatus?.toLowerCase()
                )
              ) {
                // errorToast(
                //   "Transaction failed",
                //   `${txNote}  ${transaction?.secondaryTransactionStatus?.toLowerCase()}`
                // );
                return transaction?.secondaryTransactionStatus?.toLowerCase();
              } else if (i === maxRound - 1) {
                // errorToast("Transaction timeout", `${txNote}  timeout`);
                return "pending";
              } else {
                continue;
              }
            }
          } else {
            // errorToast("Transaction failed.", "Error creating transaction");
            return null;
          }
        })
        .catch((err) => {
          if (process.env.REACT_APP_NODE_ENV !== "production") {
            console.error(
              "create transaction on hextrust failed with error: ",
              err?.message
            );
          }

          return null;
        });
    } else if (
      transactionGasRes &&
      Object.keys(transactionGasRes)?.length > 0 &&
      !balanceStatus
    ) {
      console.error(
        "Insufficient ETH Balance",
        `Top up at least ${Number(
          ethers.utils.formatEther(
            Number(
              Number(transactionGas?.average?.gasLimit) *
                Number(transactionGasRes?.average?.maxFeePerGas)
            ) - Number(userBalance)
          )
        )?.toFixed(4)} ETH more`
      );
    }

    return null;
  } else {
    return null;
  }
};

export const handleCustodyTransaction = async (
  custodyWalletDetails,
  contractAddress,
  provider,
  nativeTokenAmountInEther,
  txData,
  txNonce,
  txNote
) => {
  switch (custodyWalletDetails?.custodyId?.toLowerCase()) {
    case "hextrust":
      return await hexTrustSendTransaction(
        custodyWalletDetails?.address,
        provider,
        txNonce,
        contractAddress,
        custodyWalletDetails?.chainId,
        custodyWalletDetails?.vaultId,
        nativeTokenAmountInEther,
        txData,
        txNote
      );

    default:
      return null;
  }
};
